// ----------------------------------------------------------------
// Payment method (shared lookup from payment_methods table)
// ----------------------------------------------------------------
export interface PaymentMethod {
  id: number;
  name: string;
}

// Payment methods available for accommodation payments (id 1–5)
export const ACCOMMODATION_PAYMENT_METHODS: PaymentMethod[] = [
  { id: 1, name: 'Cash' },
  { id: 2, name: 'Card' },
  { id: 3, name: 'Mobile Money' },
  { id: 4, name: 'Cheque' },
  { id: 5, name: 'EFT' },
];

// ----------------------------------------------------------------
// Accommodation booking payment (per-payment transaction record)
// ----------------------------------------------------------------
export interface AccommodationPayment {
  id: string;
  booking_id: string;
  amount: number;
  payment_method_id: number;
  notes: string | null;
  paid_at: string;
  recorded_by: string | null;
  created_at: string;
  payment_method?: PaymentMethod;
}

export interface CreatePaymentRequest {
  booking_id: string;
  amount: number;
  payment_method_id: number;
  notes?: string | null;
}

// ----------------------------------------------------------------
// Dashboard stats (returned by get_accommodation_dashboard_stats RPC)
// ----------------------------------------------------------------
export interface BookingsBySource {
  source_name: string;
  booking_count: number;
  revenue: number;
}

export interface TopCountry {
  country_name: string;
  count: number;
}

export interface AccommodationDashboardStats {
  total_bookings: number;
  pending_bookings: number;
  confirmed_bookings: number;
  checked_in: number;
  checked_out: number;
  cancelled: number;
  no_show: number;
  total_revenue: number;
  total_paid: number;
  avg_nights: number;
  bookings_by_source: BookingsBySource[];
  top_countries: TopCountry[];
}

export type AccommodationBookingStatus =
  | 'pending'
  | 'confirmed'
  | 'checked_in'
  | 'checked_out'
  | 'cancelled'
  | 'no_show';

export type AccommodationMealPlan =
  | 'room_only'
  | 'breakfast'
  | 'half_board'
  | 'full_board';

export type AccommodationSyncStatus = 'pending' | 'success' | 'failed';

export interface Country {
  id: number;
  name: string;
  iso_code: string;
  phone_code: string | null;
}

export interface BookingSource {
  id: number;
  name: string;
  icon: string | null;
  sort_order: number;
  is_active: boolean;
}

// ----------------------------------------------------------------
// Shared client type extended with accommodation-specific fields
// ----------------------------------------------------------------
export interface AccommodationClient {
  id: string;
  company_id: number;
  first_name: string;
  last_name: string;
  email: string | null;
  contact: string | null;
  address: string | null;
  tin_number: string | null;
  nationality: string | null;
  date_of_birth: string | null;
  id_type: string | null;
  id_number: string | null;
  accommodation_notes: string | null;
  country_id: number | null;
  created_at: string;
  country?: Country;
}

// ----------------------------------------------------------------
// Property
// ----------------------------------------------------------------
export interface AccommodationProperty {
  id: string;
  outlet_id: string;
  name: string;
  address: string | null;
  timezone: string;
  currency: string;
  check_in_time: string;
  check_out_time: string;
  cancellation_policy: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  image_url?: string | null;
}

export interface UpdatePropertyRequest {
  name?: string;
  address?: string | null;
  timezone?: string;
  currency?: string;
  check_in_time?: string;
  check_out_time?: string;
  cancellation_policy?: string | null;
  image_url?: string | null;
}

// ----------------------------------------------------------------
// Room types
// ----------------------------------------------------------------
export interface AccommodationRoomType {
  id: string;
  property_id: string;
  name: string;
  description: string | null;
  capacity: number;
  base_price: number;
  amenities: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateRoomTypeRequest {
  property_id: string;
  name: string;
  description?: string | null;
  capacity?: number;
  base_price: number;
  amenities?: string[];
}

export interface UpdateRoomTypeRequest {
  name?: string;
  description?: string | null;
  capacity?: number;
  base_price?: number;
  amenities?: string[];
  is_active?: boolean;
}

// ----------------------------------------------------------------
// Rooms
// ----------------------------------------------------------------
export interface AccommodationRoom {
  id: string;
  property_id: string;
  room_type_id: string;
  room_number: string;
  floor: string | null;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  room_type?: AccommodationRoomType;
}

export interface CreateRoomRequest {
  property_id: string;
  room_type_id: string;
  room_number: string;
  floor?: string | null;
  notes?: string | null;
}

export interface UpdateRoomRequest {
  room_number?: string;
  room_type_id?: string;
  floor?: string | null;
  notes?: string | null;
  is_active?: boolean;
}

// ----------------------------------------------------------------
// Rate plans
// ----------------------------------------------------------------
export interface AccommodationRatePlan {
  id: string;
  property_id: string;
  name: string;
  description: string | null;
  meal_plan: AccommodationMealPlan | null;
  cancellation_policy: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateRatePlanRequest {
  property_id: string;
  name: string;
  description?: string | null;
  meal_plan?: AccommodationMealPlan | null;
  cancellation_policy?: string | null;
}

export interface UpdateRatePlanRequest {
  name?: string;
  description?: string | null;
  meal_plan?: AccommodationMealPlan | null;
  cancellation_policy?: string | null;
  is_active?: boolean;
}

// ----------------------------------------------------------------
// Bookings
// ----------------------------------------------------------------
export interface AccommodationBookingRoom {
  id: string;
  booking_id: string;
  room_id: string;
  room_type_id: string;
  price_per_night: number;
  nights: number;
  subtotal: number;
  room?: AccommodationRoom;
}

export interface AccommodationBooking {
  id: string;
  property_id: string;
  client_id: string;
  source: string;
  external_id: string | null;
  check_in: string;
  check_out: string;
  adults: number;
  children: number;
  total_amount: number;
  amount_paid: number;
  currency: string;
  status: AccommodationBookingStatus;
  notes: string | null;
  source_id: number | null;
  guest_country_id: number | null;
  /** True when the OTA collects payment on behalf of the property */
  ota_collects_payment: boolean;
  source_name?: string | null;
  guest_country_name?: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  client?: AccommodationClient;
  booking_rooms?: AccommodationBookingRoom[];
  booking_source?: BookingSource;
  guest_country?: Country;
}

export interface CreateBookingRequest {
  property_id: string;
  client_id: string;
  check_in: string;
  check_out: string;
  adults?: number;
  children?: number;
  total_amount: number;
  currency?: string;
  notes?: string | null;
  source_id?: number | null;
  guest_country_id?: number | null;
  rooms: Array<{
    room_id: string;
    room_type_id: string;
    price_per_night: number;
    nights: number;
    subtotal: number;
  }>;
}

export interface UpdateBookingRequest {
  check_in?: string;
  check_out?: string;
  adults?: number;
  children?: number;
  total_amount?: number;
  amount_paid?: number;
  notes?: string | null;
}

// ----------------------------------------------------------------
// Early checkout
// ----------------------------------------------------------------

export interface EarlyCheckoutRoomParams {
  /** accommodation_booking_rooms.id */
  bookingRoomId: string;
  /** accommodation_rooms.room_id (for labelling) */
  roomId: string;
  /** New ISO date string (YYYY-MM-DD) for this room's checkout */
  newCheckOut: string;
  /** Recalculated number of nights */
  nights: number;
  /** Recalculated subtotal */
  subtotal: number;
}

export interface EarlyCheckoutParams {
  bookingId: string;
  checkIn: string;
  originalCheckOut: string;
  /** Per-room updates — only rooms whose date has changed */
  rooms: EarlyCheckoutRoomParams[];
  /** New booking-level check_out (latest new date across all rooms) */
  newCheckOut: string;
  /** Recalculated total across all rooms */
  newTotalAmount: number;
  /** Whether all rooms are now checked out (determines new status) */
  allRoomsCheckedOut: boolean;
  /** If true, fire the send-booking-email edge function */
  notifyGuest: boolean;
}

// ----------------------------------------------------------------
// Booking events (immutable audit trail)
// ----------------------------------------------------------------
export type AccommodationBookingEventType =
  | 'status_change'
  | 'rate_updated'
  | 'payment_updated'
  | 'payment_deleted';

/** One before/after pair recorded by a rate or payment correction. */
export interface BookingFinancialChange {
  label: string;
  kind: 'amount' | 'text' | 'date';
  from: string | number | null;
  to: string | number | null;
}

/** metadata written by the update_booking_* / delete_booking_payment RPCs. */
export interface BookingFinancialChangeMetadata {
  currency?: string;
  changes?: BookingFinancialChange[];
  total_before?: number;
  total_after?: number;
  paid_before?: number;
  paid_after?: number;
}

export interface AccommodationBookingEvent {
  id: string;
  booking_id: string;
  event_type: AccommodationBookingEventType;
  from_status: AccommodationBookingStatus | null;
  /** Null for rate/payment correction events. */
  to_status: AccommodationBookingStatus | null;
  triggered_by: string;
  notes: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  triggered_by_user?: {
    first_name: string;
    last_name: string;
  };
}

// ----------------------------------------------------------------
// Availability check helpers
// ----------------------------------------------------------------
export interface RoomAvailabilityResult {
  room_id: string;
  is_available: boolean;
}

// ----------------------------------------------------------------
// Booking filters for list/calendar queries
// ----------------------------------------------------------------
export interface BookingFilters {
  status?: AccommodationBookingStatus;
  check_in_from?: string;
  check_in_to?: string;
  stay_from?: string;
  stay_to?: string;
  search?: string;
  check_out_before?: string;
}

// ----------------------------------------------------------------
// Guests
// ----------------------------------------------------------------

export interface AccommodationGuest {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  contact: string | null;
  address: string | null;
  nationality: string | null;
  country_name: string | null;
  id_type: string | null;
  id_number: string | null;
  created_at: string;
  total_bookings: number;
  last_visit_date: string | null;
  booking_methods: string | null;
  latest_booking_status: string | null;
  outstanding_credit_balance: number;
  outlet_id: string | null;
}

export interface AccommodationGuestPreviewItem {
  name: string;
  quantity: number;
  revenue: number;
}

export interface AccommodationGuestPreviewOpenTab {
  order_id: string;
  bill_number: number;
  sale_date: string;
  table_name: string | null;
  section_name: string | null;
  balance_amount: number;
}

export interface AccommodationGuestPreviewBooking {
  id: string;
  check_in: string;
  check_out: string;
  status: string;
  room_label: string;
  amount: number;
  currency: string;
  source_name: string | null;
  property_name: string;
}

export interface AccommodationGuestDetails {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  contact: string | null;
  address: string | null;
  country_name: string | null;
  created_at: string;
  member_since: string;
  total_bookings: number;
  total_booking_revenue: number;
  total_sales_spend: number;
  lifetime_value: number;
  avg_sales_spend: number;
  preferred_channel: string;
  common_stay_pattern: string;
  lead_time_label: string;
  cancellation_rate: number;
  preferred_room: string | null;
  preferred_payment_method: string | null;
  management_notes: string | null;
  last_visit_date: string | null;
  currency: string;
  loyalty_label: string;
  tier_label: string;
  outstanding_credit_balance: number;
  open_tabs_count: number;
  open_tabs: AccommodationGuestPreviewOpenTab[];
  top_items: AccommodationGuestPreviewItem[];
  recent_bookings: AccommodationGuestPreviewBooking[];
}

// ----------------------------------------------------------------
// Guest reviews
// ----------------------------------------------------------------

export interface GuestReview {
  id: string;
  booking_id: string;
  property_id: string;
  client_id: string | null;
  token_id: string | null;

  overall_rating: number;
  cleanliness_rating: number | null;
  service_rating: number | null;
  food_rating: number | null;
  remarks: string | null;

  submitted_at: string;
  created_at: string;
}

export interface GuestReviewAnalytics extends GuestReview {
  computed_avg_rating: number;
  guest_name: string;
  guest_email: string | null;
  guest_phone: string | null;
  check_in: string;
  check_out: string;
  nights_stayed: number;
  booking_source: string;
  property_name: string;
  outlet_id: string;
}

export interface ReviewStats {
  total_reviews: number;
  avg_overall: number | null;
  avg_cleanliness: number | null;
  avg_service: number | null;
  avg_food: number | null;
  avg_computed: number | null;
  five_star_count: number;
  four_star_count: number;
  three_star_count: number;
  two_star_count: number;
  one_star_count: number;
  total_checkouts: number;
}

export interface ReviewMonthlyTrend {
  month_label: string;
  month_start: string;
  five_star: number;
  four_star: number;
  three_star: number;
  two_star: number;
  one_star: number;
  total: number;
  avg_rating: number;
}
